import { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, CircularProgress, Dialog, DialogContent, DialogTitle,
  InputAdornment, List, ListItemButton, ListItemText, TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import type { ColumnLookupRow } from './api';

export interface LookupPickerProps {
  open: boolean;
  /** Column being edited (shown in the title). */
  column: string;
  /** Reference-table name, for the subtitle. */
  table?: string;
  /** Current cell value, highlighted in the list. */
  currentValue?: unknown;
  /** Runs the search (debounced) and returns matching display/value rows. */
  search: (text: string) => Promise<ColumnLookupRow[]>;
  onClose: () => void;
  onSelect: (row: ColumnLookupRow) => void;
}

/**
 * Searchable modal picker for a column lookup, mirroring the twasta.ai
 * transaction-editor lookup. Typing debounces a backend search; clicking a row
 * stores its ``value`` into the edited cell.
 */
export default function LookupPickerDialog({
  open, column, table, currentValue, search, onClose, onSelect,
}: LookupPickerProps) {
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<ColumnLookupRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<number | null>(null);
  // Bumps on every fired request so a slow earlier response can't overwrite a
  // newer one (last-write-wins by sequence).
  const seqRef = useRef(0);

  // Reset and load the first page whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setError(null);
    runSearch('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const runSearch = (text: string) => {
    const seq = ++seqRef.current;
    setLoading(true);
    setError(null);
    search(text)
      .then((r) => { if (seq === seqRef.current) setRows(r); })
      .catch((e) => { if (seq === seqRef.current) setError((e as Error).message); })
      .finally(() => { if (seq === seqRef.current) setLoading(false); });
  };

  const onQueryChange = (text: string) => {
    setQuery(text);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => runSearch(text), 250);
  };

  const sameValue = (a: unknown, b: unknown) =>
    a != null && b != null && String(a) === String(b);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        Look up {column}
        {table && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            from {table}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus fullWidth size="small" placeholder="Search…"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          sx={{ mb: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
            endAdornment: loading ? <CircularProgress size={16} /> : undefined,
          }}
        />
        {error && <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert>}
        {!error && !loading && rows.length === 0 && (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 2, textAlign: 'center' }}>
            No matches.
          </Typography>
        )}
        <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
          <List dense disablePadding>
            {rows.map((r, i) => (
              <ListItemButton
                key={`${String(r.value)}-${i}`}
                selected={sameValue(r.value, currentValue)}
                onClick={() => { onSelect(r); onClose(); }}
              >
                <ListItemText
                  primary={r.display || String(r.value ?? '')}
                  secondary={
                    r.display && String(r.value) !== r.display ? String(r.value ?? '') : undefined
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
